import React from 'react';
import { Flex, Box, List, ListItem } from '@opendoor/bricks/core';
import _ from 'lodash';

const MAX_VISIBLE_DOTS = 5;
const ANIMATION_WIDTH = 15;
const MAX_PROGRESS_CONTAINER_WIDTH = 75;
const MAX_DOT_WIDTH = 10;
interface ProgressIndicatorProps {
  currentIndex: number;
  photoCount: number;
}

/**
 * Progress indicator for PropertyImageCarousel
 *
 * storybook-check-ignore
 */
export const PropertyImageCarouselProgress: React.FC<ProgressIndicatorProps> = ({
  currentIndex,
  photoCount,
}) => {
  /**
   * This function calculates the max width of the progress indicator container.
   * max width is needed to create the animation effect. The idea is that at any given time,
   * only 5 dots will be available in this 75 px container. The dots are moved to the left / right
   * as you scroll the images to account for this and for the animation effect.
   */
  const calculateWindowWidth = () => {
    return Math.min(MAX_PROGRESS_CONTAINER_WIDTH, photoCount * ANIMATION_WIDTH);
  };

  /**
   * This function calculates the dot size. The expectations are
   * 1) The dot corresponding to the current photo should be of size 10px
   * 2) the dots corresponding to the adjacent photos of the current index should be of size 8px
   * 3) all other dots should be of size 7px
   */
  const calculateDotSize = (index: number) => {
    return (
      MAX_DOT_WIDTH - Math.min(3, Math.abs(currentIndex - index) + (index != currentIndex ? 1 : 0))
    );
  };

  /* Calculates opacity for the dots */
  const calculateOpacity = (index: number) => {
    return index != currentIndex ? 0.6 : 1;
  };

  /**
   * This function calculates the distance to be moved by the progress
   * indicator container for the animation effect. The expectation is that if more than
   * 5 photos are available , the dot corresponding to those photos should be in the middle
   * except for the penultimate and last photos for which the positions remain the same.
   */
  const calculateAnimationDistance = (index: number) => {
    if (index <= 2 || photoCount <= MAX_VISIBLE_DOTS) {
      return 0;
    }

    /*
        Explanation: let's say there are 14 photos available. so for all photos from 3 to 12 , the
        main dot should appear in the middle ie in the 3rd position => we have to move the container by 2 positions
        for photos 13 and 14 we have to move the containers by 3 and 4 positions respectively.
    */
    return (
      -(
        index - Math.max(Math.floor(MAX_VISIBLE_DOTS / 2), MAX_VISIBLE_DOTS - (photoCount - index))
      ) * ANIMATION_WIDTH
    );
  };

  return (
    <Flex position="absolute" bottom={4} alignItems="center" justifyContent="center" width="100%">
      <Flex width={calculateWindowWidth()} overflow="hidden">
        <List
          variant="text"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          transform={`translateX(${calculateAnimationDistance(currentIndex)}px)`}
          transition="transform 0.3s ease-in"
        >
          {_.range(photoCount).map((_, idx) => {
            return (
              <ListItem key={idx.toString()}>
                <Box
                  height={calculateDotSize(idx)}
                  width={calculateDotSize(idx)}
                  borderRadius="rounded"
                  backgroundColor="neutrals0"
                  opacity={calculateOpacity(idx)}
                  flexShrink={0}
                  marginRight={3}
                  data-testid={`${calculateDotSize(idx)}-progress-indicator`}
                  aria-current={currentIndex === idx ? 'true' : 'false'}
                />
              </ListItem>
            );
          })}
        </List>
      </Flex>
    </Flex>
  );
};
